import React, { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useLookups } from "@/context/LookupContext";
import { useI18n } from "@/context/I18nContext";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";

async function compressImage(file, maxBytes = 1024 * 1024, maxDim = 1920) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  try {
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    const img = await new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = rej;
      im.src = dataUrl;
    });
    let width = img.width, height = img.height;
    if (width > maxDim || height > maxDim) {
      const scale = Math.min(maxDim / width, maxDim / height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const draw = () => { canvas.width = width; canvas.height = height; ctx.drawImage(img, 0, 0, width, height); };
    draw();
    let quality = 0.9;
    let blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    while (blob && blob.size > maxBytes && quality > 0.4) {
      quality -= 0.1;
      blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    }
    while (blob && blob.size > maxBytes && width > 640) {
      width = Math.round(width * 0.8); height = Math.round(height * 0.8);
      draw();
      blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.7));
    }
    if (!blob) return file;
    const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg" });
  } catch {
    return file;
  }
}

function buildInitial(fields, initial) {
  const obj = {};
  fields.forEach((f) => {
    const fallback = f.type === "gallery" ? [] : (f.default ?? "");
    obj[f.name] = initial && initial[f.name] != null ? initial[f.name] : fallback;
  });
  return obj;
}

export function EntityDialog({ open, onClose, title, fields, initial, onSubmit, testid, beforeSubmit }) {
  const { lookups } = useLookups();
  const { t } = useI18n();
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) setValues(buildInitial(fields, initial));
  }, [open, initial, fields]);

  const setVal = (name, v) => setValues((prev) => ({ ...prev, [name]: v }));

  const optionsFor = (f) => {
    if (f.options) return f.options;
    if (f.lookup) {
      const list = lookups[f.lookup] || [];
      return list.map((x) => ({
        value: x.id,
        label: f.lookup === "properties" ? x.name
          : f.lookup === "rooms" ? `${x.room_number}`
          : f.lookup === "tenants" ? x.name
          : f.lookup === "vendors" ? x.name
          : f.lookup === "contracts" ? x.contract_number
          : x.name,
      }));
    }
    return [];
  };

  const handleFile = async (f, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", await compressImage(file));
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setVal(f.name, data.url);
      toast.success("アップロードしました");
    } catch {
      toast.error("アップロードに失敗しました");
    } finally {
      setUploading(false);
    }
  };

  const handleGallery = async (f, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", await compressImage(file));
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setVal(f.name, [...(values[f.name] || []), data.url]);
      toast.success("アップロードしました");
    } catch {
      toast.error("アップロードに失敗しました");
    } finally {
      setUploading(false);
    }
  };
  const removeGallery = (f, i) => setVal(f.name, (values[f.name] || []).filter((_, j) => j !== i));

  const submit = async () => {
    for (const f of fields) {
      if (f.required && (values[f.name] === "" || values[f.name] == null)) {
        toast.error(`${t(f.label)}を入力してください`);
        return;
      }
      if (f.validate) {
        const err = f.validate(values, !!initial);
        if (err) { toast.error(t(err)); return; }
      }
    }
    const payload = {};
    fields.forEach((f) => {
      if (f.type === "custom") return;
      let v = values[f.name];
      if (f.type === "number" || f.type === "currency") v = v === "" ? 0 : Number(v);
      payload[f.name] = v;
    });
    setSaving(true);
    try {
      if (beforeSubmit) await beforeSubmit(values, !!initial);
      await onSubmit(payload);
      onClose();
    } catch (e) {
      // handled by caller toast
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="text-xl font-medium">{t(title)}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
          {fields.map((f) => {
            if (f.type === "hidden") return null;
            if (f.type === "custom") return <div key={f.name} className="md:col-span-2">{f.render({ values, setVal })}</div>;
            const full = f.type === "textarea" || f.type === "file" || f.type === "gallery" || f.colSpan === 2;
            return (
              <div key={f.name} className={full ? "md:col-span-2" : ""}>
                <Label className="text-xs text-muted-foreground mb-1.5 block">
                  {t(f.label)}{f.required && <span className="text-brand ml-0.5">*</span>}
                </Label>
                {f.type === "textarea" ? (
                  <Textarea
                    value={values[f.name] ?? ""}
                    onChange={(e) => setVal(f.name, e.target.value)}
                    rows={3}
                    data-testid={`field-${f.name}`}
                  />
                ) : f.type === "select" ? (
                  <select
                    value={values[f.name] ?? ""}
                    onChange={(e) => setVal(f.name, e.target.value)}
                    className="w-full h-10 px-3 rounded-sm border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
                    data-testid={`field-${f.name}`}
                  >
                    <option value="">{t("選択してください")}</option>
                    {optionsFor(f).map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                ) : f.type === "gallery" ? (
                  <div className="flex flex-wrap gap-2">
                    {(values[f.name] || []).map((u, i) => (
                      <div key={i} className="relative w-20 h-20 rounded-sm overflow-hidden border border-input">
                        <a href={fileUrl(u)} target="_blank" rel="noreferrer"><img src={fileUrl(u)} alt="" className="w-full h-full object-cover" /></a>
                        <button type="button" onClick={() => removeGallery(f, i)} className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs leading-none" data-testid={`field-${f.name}-remove-${i}`}>×</button>
                      </div>
                    ))}
                    <label className="w-20 h-20 flex flex-col items-center justify-center gap-1 border border-dashed border-input rounded-sm cursor-pointer hover:bg-gray-50 text-[11px] text-muted-foreground">
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4" />追加</>}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleGallery(f, e.target.files[0])} data-testid={`field-${f.name}`} />
                    </label>
                  </div>
                ) : f.type === "file" ? (
                  <div className="flex items-center gap-3">
                    {values[f.name] && (
                      <a href={fileUrl(values[f.name])} target="_blank" rel="noreferrer" className="block w-20 h-20 rounded-sm overflow-hidden border border-input flex-shrink-0" data-testid={`field-${f.name}-preview`}>
                        <img src={fileUrl(values[f.name])} alt="" className="w-full h-full object-cover" />
                      </a>
                    )}
                    <label className="inline-flex items-center gap-2 px-3 h-10 border border-input rounded-sm cursor-pointer hover:bg-gray-50 text-sm">
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {values[f.name] ? t("変更") : t("ファイル選択")}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(f, e.target.files[0])} data-testid={`field-${f.name}`} />
                    </label>
                  </div>
                ) : (
                  <Input
                    type={f.type === "currency" ? "number" : (f.type || "text")}
                    value={values[f.name] ?? ""}
                    onChange={(e) => setVal(f.name, e.target.value)}
                    data-testid={`field-${f.name}`}
                  />
                )}
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="dialog-cancel">{t("キャンセル")}</Button>
          <Button onClick={submit} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid="dialog-save">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{t("保存")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
